// Requires vue-resource. See https://github.com/pagekit/vue-resource
// Refer to the documentation at https://marketplace-api.fanxchange.com
import Cart from '../types/Cart'

export default {
    install (Vue, options) {
        if (!Vue.http) {
            throw new Error('This module requires the vue-resource plugin to be registered on the Vue instance.')
        }

        function buildPath (path) {
            return options.basePath ? options.basePath + path : path
        }

        Vue.prototype.$marketplace = {
            login (email, password) {
                return Vue.http
                    .post(buildPath('/v2/login'), {
                        email_address: email,
                        password
                    })
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            logout () {
                return Vue.http
                    .post(buildPath('/v2/logout'))
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            initSession (partnerKey) {
                return Vue.http
                    .post(buildPath(`/v2/sso/auth/${partnerKey}`))
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            signup (payload) {
                return Vue.http
                    .post(buildPath('/v2/signup'), payload)
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            requestPasswordReset (payload) {
                return Vue.http
                    .post(buildPath('/v2/reset-password'), payload)
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            resetPassword (token, payload) {
                return Vue.http
                    .post(buildPath(`/v2/reset-password/${token}`), payload)
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getBraintreeClientToken () {
                return Vue.http
                    .get(buildPath('/v2/payment/client-token'))
                    .then(
                        (response) => Promise.resolve(response.body.token),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getProfile (accountId) {
                return Vue.http
                    .get(buildPath(`/v2/accounts/${accountId}/profile`))
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getPromoAnnouncement (config = {}) {
                return Vue.http
                    .get(buildPath('/v2/content/announcement'), config)
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getAccountDetails (accountId) {
                return Vue.http
                    .get(buildPath(`/v2/accounts/${accountId}`))
                    .then(
                        (response) => Promise.resolve(response.body.account),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getOrderHistory (accountId) {
                return Vue.http
                    .get(buildPath(`/v2/accounts/${accountId}/orders`))
                    .then(
                        (response) => Promise.resolve(response.body.orders),
                        (response) => Promise.reject(response.body)
                    )
            },
            getOrder (accountId, orderId) {
                return Vue.http
                    .get(buildPath(`/v2/accounts/${accountId}/orders/${orderId}`))
                    .then(
                        (response) => Promise.resolve(response.body.order),
                        (response) => Promise.reject(response.body)
                    )
            },
            updateProfile (accountId, profile) {
                return Vue.http
                    .post(buildPath(`/v2/accounts/${accountId}/profile`), profile)
                    .then((response) => Promise.resolve(response.body), (response) => Promise.reject(response.body))
            },
            changePassword (accountId, params) {
                return Vue.http
                    .post(buildPath(`/v2/accounts/${accountId}/security`), params)
                    .then((response) => Promise.resolve(response.body), (response) => Promise.reject(response.body))
            },
            getShippingAddresses (accountId) {
                return Vue.http
                    .get(buildPath(`/v2/accounts/${accountId}/addresses/shipping`))
                    .then((response) => Promise.resolve(response.body), (response) => Promise.reject(response.body))
            },
            addShippingAddress (accountId, params) {
                return Vue.http
                    .post(buildPath(`/v2/accounts/${accountId}/addresses/shipping`), params)
                    .then((response) => Promise.resolve(response.body), (response) => Promise.reject(response.body))
            },
            updateShippingAddress (accountId, addressId, params) {
                return Vue.http
                    .post(buildPath(`/v2/accounts/${accountId}/addresses/shipping/${addressId}`), params)
                    .then((response) => Promise.resolve(response.body), (response) => Promise.reject(response.body))
            },
            deleteShippingAddress (accountId, addressId) {
                return Vue.http
                    .delete(buildPath(`/v2/accounts/${accountId}/addresses/shipping/${addressId}`))
                    .then((response) => Promise.resolve(response.body), (response) => Promise.reject(response.body))
            },
            getPaymentMethods (accountId) {
                return Vue.http
                    .get(buildPath(`/v2/accounts/${accountId}/payments`))
                    .then((response) => Promise.resolve(response.body), (response) => Promise.reject(response.body))
            },
            addPaymentMethod (accountId, params) {
                return Vue.http
                    .post(buildPath(`/v2/accounts/${accountId}/payments`), params)
                    .then((response) => Promise.resolve(response.body), (response) => Promise.reject(response.body))
            },
            updatePaymentMethod (accountId, paymentId, params) {
                return Vue.http
                    .post(buildPath(`/v2/accounts/${accountId}/payments/${paymentId}`), params)
                    .then((response) => Promise.resolve(response.body), (response) => Promise.reject(response.body))
            },
            deletePaymentMethod (accountId, paymentId) {
                return Vue.http
                    .delete(buildPath(`/v2/accounts/${accountId}/payments/${paymentId}`))
                    .then((response) => Promise.resolve(response.body), (response) => Promise.reject(response.body))
            },
            search (query, params = {}, config = {}) {
                params.q = query
                params.page = params.page || 1
                config = { ...config, params }
                return Vue.http
                    .get(buildPath('/v2/events/search'), config)
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            autocomplete (query, config) {
                config = { ...config, params: { q: query } }
                return Vue.http
                    .get(buildPath('/v2/events/autocomplete'), config)
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getCity (cityId, config = {}) {
                return Vue.http
                    .get(buildPath(`/v2/cities/${cityId}`, config))
                    .then((response) => Promise.resolve(response.body.city))
                    .catch((response) => Promise.reject(response.body.errors))
            },
            getLocalEvents (params = {}) {
                params.page = params.page || 1
                return Vue.http
                    .get(buildPath('/v2/events/nearby'), { params })
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getLocalEventsCategorized (params = {}) {
                params.page = params.page || 1
                return Vue.http
                    .get(buildPath('/v2/events/nearby-groupby-segment'), {
                        params
                    })
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getEntityEvents (type, id, params) {
                return Vue.http
                    .get(buildPath(`/v2/events/${type}/${id}`), { params })
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getEvent (eventId) {
                return Vue.http
                    .get(buildPath(`/v2/events/${eventId}`))
                    .then(
                        (response) => Promise.resolve(response.body.event),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getEvents (params) {
                return Vue.http
                    .get(buildPath('/v2/events'), { params })
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getExclusiveEvents (params) {
                return Vue.http
                    .get(buildPath('/v2/events/exclusive'), { params })
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getLocation (params, config = {}) {
                const request = { params, ...config }
                return Vue.http
                    .get(buildPath('/v2/locations'), request)
                    .then(
                        (response) => Promise.resolve(response.body.location),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getPerformer (performerId) {
                return Vue.http
                    .get(buildPath(`/v2/performers/${performerId}`))
                    .then(
                        (response) => Promise.resolve(response.body.performer),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getPerformersEvents (params) {
                return Vue.http
                    .get(buildPath('/v2/events/select-performers'), { params })
                    .then(
                        (response) => Promise.resolve(response.body.results),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getLocalPerformers (params) {
                return Vue.http
                    .get(buildPath('/v2/performers/nearby'), { params })
                    .then(
                        (response) => Promise.resolve(response.body.performers),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            searchLocations (query) {
                return Vue.http
                    .get(buildPath('/v2/locations/autocomplete'), {
                        params: {
                            q: query
                        }
                    })
                    .then(
                        (response) => Promise.resolve(response.body.locations),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getTickets (eventId, params, config) {
                config = { ...config, params }
                return Vue.http
                    .get(buildPath(`/v2/events/${eventId}/tickets`), config)
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getTicketSummary (ticketId, params) {
                return Vue.http
                    .get(buildPath(`/v2/tickets/${ticketId}/summary`), {
                        params
                    })
                    .then(
                        (response) => Promise.resolve(response.body.summary),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getTicketInsuranceQuote (ticketId, params) {
                return Vue.http
                    .get(buildPath(`/v2/tickets/${ticketId}/insurance-quote`), {
                        params
                    })
                    .then(
                        (response) => Promise.resolve(response.body.insurance),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getTicketPricing (ticketId, params, config = {}) {
                config = { ...config, params }
                return Vue.http
                    .get(buildPath(`/v2/tickets/${ticketId}/pricing`), config)
                    .then((response) => Promise.resolve(response.body))
                    .catch((response) => {
                        if (response.status === 0) {
                            return Promise.reject([{ code: 'request-canceled' }])
                        }
                        return Promise.reject(response.body.errors)
                    })
            },
            getTicketPricingPOST (ticketId, params, config = {}) {
                return Vue.http
                    .post(buildPath(`/v2/tickets/${ticketId}/pricing`), params, config)
                    .then((response) => Promise.resolve(response.body))
                    .catch((response) => {
                        if (response.status === 0) {
                            return Promise.reject([{ code: 'request-canceled' }])
                        }
                        return Promise.reject(response.body.errors)
                    })
            },
            placeOrder (order, config) {
                return Vue.http
                    .post(buildPath('/v2/orders'), order, config || {})
                    .then(
                        (response) => Promise.resolve(response.body.order),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getTermsAndConditions () {
                return Vue.http
                    .get(buildPath('/v2/content/terms-and-conditions'))
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getFAQs () {
                return Vue.http
                    .get(buildPath('/v2/content/faqs'))
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            optInRemarketing (cart) {
                if (!(cart instanceof Cart)) {
                    throw new Error('You need a Cart')
                }
                return Vue.http
                    .post(buildPath('/v2/carts'), {
                        email: cart.emailAddress,
                        event_id: cart.eventId,
                        ticket_id: cart.ticketId
                    })
                    .then(
                        (response) => Promise.resolve(response.body.cart),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getPrivacyPolicy () {
                return Vue.http
                    .get(buildPath('/v2/content/privacy-policy'))
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            getVenue (venueId, config = {}) {
                return Vue.http
                    .get(buildPath(`/v2/venues/${venueId}`, config))
                    .then((response) => Promise.resolve(response.body.venue))
                    .catch((response) => Promise.reject(response.body.errors))
            },
            getConfig () {
                return Vue.http
                    .get(buildPath('/v2/configuration'))
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            },
            validateOrder (order) {
                return Vue.http
                    .post(buildPath('/v2/orders/validate'), order)
                    .then(
                        (response) => Promise.resolve(response.body),
                        (response) => Promise.reject(response.body.errors)
                    )
            }
        }
    }
}
